import styled, { css, keyframes } from "styled-components";

import SpinnerLoading from "../SpinnerLoading";
import AnimationDirection from "./interfaces/AnimationDirection";
import HorizontalAlign from "./interfaces/HorizontalAlign";
import VerticalAlign from "./interfaces/VerticalAlign";

const horizontal = {
  center: "center",
  left: "flex-start",
  right: "flex-end",
};

const vertical = {
  center: "center",
  top: "flex-start",
  bottom: "flex-end",
};

function GetTransform(animFrom: AnimationDirection) {
  const transformAnimation = {
    top: [0, "-200px"],
    bottom: [0, "200px"],
    left: ["-200px", 0],
    right: ["200px", 0],
  }[animFrom];

  const translateX = transformAnimation?.[0] || 0;
  const translateY = transformAnimation?.[1] || 0;

  return `translate3d(${translateX}, ${translateY}, 0)`;
}

const modalShow = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const mobileModalContentShow = keyframes`
  from {
    transform: translateY(10vh);
  }
  to {
    transform: translateY(0px);
  }
`;

const modalContentShow = ({ animationFrom }: ModalContainerAttrs) => keyframes`
  from {
    transform: ${GetTransform(animationFrom)} scale(0.8);
  }
  to {
    transform: translateY(0px) scale(1);
  }
`;

interface ModalContainerAttrs {
  animationFrom?: AnimationDirection;
  horizontalAlign: HorizontalAlign;
  verticalAlign: VerticalAlign;
  mobileResponsive?: boolean;
}

export const ModalContainer = styled.div<ModalContainerAttrs>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: ${({ horizontalAlign }) => horizontal[horizontalAlign]};
  align-items: ${({ verticalAlign }) => vertical[verticalAlign]};
  padding: 0 calc((100vw - ${({ theme }) => theme.max_screen_width}px) / 2);
  pointer-events: none;
  opacity: 0;
  z-index: 800;

  &.bg-filter {
    background-color: rgba(0, 0, 0, 0.9);
  }

  & > *:first-child:not(${SpinnerLoading}) {
    @media (max-width: ${({ theme }) => theme.mobile_max_width}px) {
      transform: translateY(10vh);

      ${({ mobileResponsive }) =>
        mobileResponsive &&
        css`
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          width: 100% !important;
          height: 100% !important;
          max-height: 100% !important;
          max-width: 100% !important;
          margin: 0 !important;
          border-radius: 0 !important;
        `}
    }

    @media (min-width: ${({ theme }) => theme.mobile_max_width + 1}px) {
      transform: ${({ animationFrom }) => GetTransform(animationFrom)} scale(0.8);
    }
  }

  &[data-show~="true"] {
    pointer-events: auto;
    opacity: 1;

    & > *:first-child {
      transform: none;
    }
  }

  &[data-animated~="true"] {
    transition: opacity 0.2s;

    & > *:first-child {
      transition: transform 0.2s;
    }
  }

  &[data-show~="true"][data-animated~="true"] {
    animation: ${modalShow} 0.3s;

    & > *:first-child:not(${SpinnerLoading}) {
      @media (max-width: ${({ theme }) => theme.mobile_max_width}px) {
        animation: ${mobileModalContentShow} 0.3s;
      }

      @media (min-width: ${({ theme }) => theme.mobile_max_width + 1}px) {
        animation: ${modalContentShow} 0.3s;
      }
    }
  }
`;
